import { useState } from "react";
import { Form, required, useNotify, useTranslate } from "ra-core";
import { useSetPassword, useSupabaseAccessToken } from "ra-supabase-core";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { cn } from "@/lib/utils";
import { AuthLayout } from "@/components/atomic-crm/login/AuthLayout";
import {
  authSubmitButtonClassName,
  glassFormClassName,
  glassInputClassName,
} from "@/components/atomic-crm/login/authStyles";

interface SetPasswordFormData {
  password: string;
  confirmPassword: string;
}

export const SetPasswordPage = () => {
  const [loading, setLoading] = useState(false);

  const access_token = useSupabaseAccessToken();
  const refresh_token = useSupabaseAccessToken({
    parameterName: "refresh_token",
  });

  const notify = useNotify();
  const translate = useTranslate();
  const [, { mutateAsync: setPassword }] = useSetPassword();

  const validate = (values: SetPasswordFormData) => {
    if (values.password !== values.confirmPassword) {
      return {
        password: "ra-supabase.validation.password_mismatch",
        confirmPassword: "ra-supabase.validation.password_mismatch",
      };
    }
    return {};
  };

  if (!access_token || !refresh_token) {
    if (process.env.NODE_ENV === "development") {
      console.error("Missing access_token or refresh_token for set password");
    }
    return (
      <AuthLayout
        title={translate("ra-supabase.set_password.new_password", {
          _: "Choose your password",
        })}
      >
        <p className="text-center text-sm text-red-200">
          {translate("ra-supabase.auth.missing_tokens")}
        </p>
      </AuthLayout>
    );
  }

  const submit = async (values: SetPasswordFormData) => {
    try {
      setLoading(true);
      await setPassword({
        access_token,
        refresh_token,
        password: values.password,
      });
    } catch (error: any) {
      notify(
        typeof error === "string"
          ? error
          : typeof error === "undefined" || !error.message
            ? "ra.auth.sign_in_error"
            : error.message,
        {
          type: "warning",
          messageArgs: {
            _:
              typeof error === "string"
                ? error
                : error && error.message
                  ? error.message
                  : undefined,
          },
        },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={translate("ra-supabase.set_password.new_password", {
        _: "Choose your password",
      })}
    >
      <Form
        className={cn("space-y-6", glassFormClassName)}
        onSubmit={submit as any}
        validate={validate as any}
      >
        <TextInput
          label={translate("ra.auth.password", {
            _: "Password",
          })}
          autoComplete="new-password"
          source="password"
          type="password"
          validate={required()}
          inputClassName={glassInputClassName}
        />
        <TextInput
          label={translate("crm.auth.confirm_password", {
            _: "Confirm password",
          })}
          source="confirmPassword"
          type="password"
          validate={required()}
          inputClassName={glassInputClassName}
        />
        <Button
          type="submit"
          className={authSubmitButtonClassName}
          disabled={loading}
        >
          {translate("ra.action.save")}
        </Button>
      </Form>
    </AuthLayout>
  );
};

SetPasswordPage.path = "set-password";
